
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { 
  PricingPlan, 
  UserSubscription, 
  UserCredits,
  UserTokenUsage 
} from "./types";
import {
  fetchPlans,
  fetchUserSubscription,
  checkTestMode,
  formatPrice,
  handleFreeSubscription,
  initiateStripeCheckout,
  isPaidPlan,
  fetchUserCredits,
  fetchUserTokenUsage
} from "./subscriptionUtils";

export function useSubscription() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [tokenUsage, setTokenUsage] = useState<UserTokenUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [isTestMode, setIsTestMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());

  const isCurrentPlan = (planId: string) => {
    return currentSubscription?.plan_id === planId;
  };

  const handleSubscribe = async (planId: string, promoCode?: string) => {
    if (!user) {
      toast.error("Please log in to subscribe");
      window.location.href = "/auth";
      return;
    }

    const selectedPlan = plans.find(p => p.id === planId);
    if (selectedPlan?.type === "free") {
      try {
        setLoading(true);
        const success = await handleFreeSubscription(user.id, planId);
        
        if (success) {
          toast.success("Successfully switched to the free plan");
          refreshData();
        } else {
          throw new Error("Failed to switch to free plan");
        }
      } catch (error: any) {
        console.error("Error switching to free plan:", error);
        toast.error(error.message || "Failed to switch to free plan");
        setError(error.message || "Failed to switch to free plan");
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`Initiating subscription in ${isTestMode ? 'TEST' : 'LIVE'} mode`);
      
      // Detailed logging for debugging
      console.log("Subscription request payload:", {
        planId,
        userId: user.id,
        promoCode: promoCode || undefined
      });
      
      const result = await initiateStripeCheckout(user.id, planId, promoCode);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      window.location.href = result.url!;
    } catch (error: any) {
      console.error("Subscription error:", error);
      const errorMessage = error.message || "Failed to process subscription";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = useCallback(() => {
    // Always fetch pricing plans, regardless of user login status
    console.log("Fetching pricing plans for all users");
    
    fetchPlans().then(data => {
      console.log("Fetched plans:", data);
      setPlans(data);
      setIsTestMode(checkTestMode(data));
    }).catch(err => {
      console.error("Error fetching plans:", err);
      setError("Failed to load pricing plans");
    });
    
    // Only fetch user-specific data if user is logged in
    if (!user) {
      setLoading(false);
      return;
    }
    
    console.log("Fetching subscription data for user:", user.id);
    setLoading(true);
    setError(null);
    setLastRefreshTime(new Date());
    
    Promise.all([
      fetchUserSubscription(user.id).then(data => {
        console.log("Fetched subscription:", data);
        setCurrentSubscription(data);
      }),
      fetchUserCredits(user.id).then(data => {
        console.log("Fetched user credits:", data);
        setUserCredits(data);
        setTokenUsage(data); // For backward compatibility
      })
    ]).catch(err => {
      console.error("Error refreshing subscription data:", err);
      setError("Failed to load subscription data. Please try again.");
    }).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Get the current plan type (free, monthly, yearly)
  const getCurrentPlanType = useCallback(() => {
    if (!currentSubscription || !plans.length) return null;
    
    const currentPlan = plans.find(p => p.id === currentSubscription.plan_id);
    return currentPlan?.type || null;
  }, [currentSubscription, plans]);

  // Check if the current plan is a paid plan
  const isOnPaidPlan = useCallback(() => {
    const planType = getCurrentPlanType();
    return isPaidPlan(planType);
  }, [getCurrentPlanType]);

  return {
    plans,
    currentSubscription,
    userCredits,
    tokenUsage, // For backward compatibility
    loading,
    error,
    isCurrentPlan,
    formatPrice,
    handleSubscribe,
    refreshData,
    isTestMode,
    lastRefreshTime,
    isOnPaidPlan,
    getCurrentPlanType
  };
}
